/**
 * Raid Provider
 *
 * React Context provider for co-op raid state management.
 * Handles lobby, room selection, draft, battle, and rewards phases.
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  Dispatch,
  SetStateAction,
} from "react";
import { useAuth } from "@/providers/auth-provider";
import { getCurrentRaid } from "@/services/raid/get-current-raid";
import { RaidWebSocketService } from "@/services/raid/websocket-service";
import {
  ServerMessage,
  WebSocketConnectionStatus,
  RaidDifficulty,
  RaidPlayerInfo,
} from "types/raids/types";
import { RaidRoomOption } from "types/raids/rooms";
import {
  PlayerRole,
  RAID_USABLE_ITEMS,
  RaidPokemon,
  RaidUsableItem,
  RaidStatus,
  SlotConfig,
} from "types/raid-dynamodb";
import { createRaid } from "@/services/raid/create-raid";
import { joinRaid as joinRaidService } from "@/services/raid/join-raid";
import { listInventoryItems } from "@/services/inventory/list-inventory-items";
import { PokeType } from "types/pokemon/poke-type";
import { RaidConfig } from "game/data/raids";

// ============================================================================
// Usable Item Types
// ============================================================================

/** Player's usable item with quantity */
export interface UsableItem {
  item_name: RaidUsableItem;
  amount: number;
}

/** Item use event for visual feedback */
export interface ItemUseEvent {
  id: string;
  playerRole: PlayerRole;
  playerName: string;
  itemName: string;
  targetPokemonId: number;
  targetPokemonName: string;
  newHp?: number;
  wasRevived: boolean;
  timestamp: number;
}

// ============================================================================
// State Types
// ============================================================================

/** Lobby player slot info */
export interface RaidLobbyPlayer {
  id: string;
  role: PlayerRole;
  username: string;
  country: string | null;
  team_preview: Array<{
    display_id: string;
    base_pokemon_name: string;
    level: number;
  }>;
  is_ready: boolean;
}

/** Room selection state */
export interface RoomSelectionState {
  round_index: number;
  duration_seconds: number;
  room_options: RaidRoomOption[];
  votes: Record<PlayerRole, number>; // player_role -> room_index
  started_at: string;
  time_remaining: number;
}

/** Draft phase state */
export interface DraftState {
  round_index: number;
  duration_seconds: number;
  picks_required: number;
  available_pokemon: RaidPokemon[];
  selected_room: RaidRoomOption;
  picks: Record<PlayerRole, number[]>; // player_role -> pokemon_ids
  slots: SlotConfig[];
  started_at: string;
  time_remaining: number;
}

/** Battle state */
export interface RaidBattleState {
  round_index: number;
  battle_log_url: string;
  team_pokemon: RaidPokemon[];
}

/** Raid result (victory or defeat) */
export interface RaidResult {
  outcome: "victory" | "defeat" | "abandoned";
  coins_earned: number;
  experience_earned: number;
  rewards?: {
    recruitment_pool: string[];
    items: string[];
  };
  boss_drops?: string[];
  defeat_round?: number;
  quitter_name?: string;
}

/** Main raid state */
export interface CoopRaidState {
  status:
    | "idle"
    | "connecting"
    | "lobby"
    | "starting"
    | "room_selection"
    | "draft"
    | "battle_starting"
    | "in_battle"
    | "battle_result"
    | "finished"
    | "error";

  // Connection
  connection_status: "disconnected" | "connected";
  raid_id: string | null;
  invite_code: string | null;

  // Lobby state
  raid_name: string | null;
  raid_type: PokeType | null;
  difficulty: RaidDifficulty;
  your_role: PlayerRole | null;
  is_host: boolean;
  players: Record<PlayerRole, RaidLobbyPlayer | null>;

  // In-raid state
  current_round: number;
  is_boss_round: boolean;
  room_selection: RoomSelectionState | null;
  draft: DraftState | null;
  current_battle: RaidBattleState | null;

  // Pokemon HP tracking
  pokemon_hp: Record<number, number>; // pokemon_id -> current_hp
  fainted_pokemon: number[];

  // Result
  result: RaidResult | null;

  // Error
  error: { code: string; message: string } | null;
}

const initialRaidState: CoopRaidState = {
  status: "idle",
  connection_status: "disconnected",
  raid_id: null,
  invite_code: null,
  raid_name: null,
  raid_type: null,
  difficulty: RaidDifficulty.NORMAL,
  your_role: null,
  is_host: false,
  players: {
    player_a: null,
    player_b: null,
    player_c: null,
    player_d: null,
  },
  current_round: 0,
  is_boss_round: false,
  room_selection: null,
  draft: null,
  current_battle: null,
  pokemon_hp: {},
  fainted_pokemon: [],
  result: null,
  error: null,
};

// ============================================================================
// Context Types
// ============================================================================

interface RaidContextValue {
  // State
  raidState: CoopRaidState;
  setRaidState: Dispatch<SetStateAction<CoopRaidState>>;

  // Lobby Actions
  createRaidLobby: (
    token: string,
    playerId: string,
    raidConfig: RaidConfig,
    difficulty: RaidDifficulty,
  ) => Promise<{ success: boolean; error?: string; invite_code?: string }>;
  joinRaidLobby: (
    token: string,
    playerId: string,
    inviteCode: string,
  ) => Promise<{ success: boolean; error?: string }>;
  toggleReady: () => void;
  setDifficulty: (difficulty: RaidDifficulty) => void;
  startRaid: () => void;
  leaveRaid: () => void;

  // In-Raid Actions
  voteForRoom: (roomIndex: number) => void;
  pickPokemon: (pokemonId: number, slotIndex: number) => void;
  unpickPokemon: (pokemonId: number) => void;
  submitPicks: () => void;
  useItem: (itemName: string, targetPokemonId: number) => void;
  acknowledgeBattleEnd: () => void;

  // Items
  usableItems: UsableItem[];
  isLoadingItems: boolean;
  itemUseEvents: ItemUseEvent[];
  clearItemUseEvent: (id: string) => void;

  // Connection
  disconnectFromRaid: () => void;

  // Helpers
  isConnected: boolean;
  isReconnecting: boolean;
  canStart: boolean;
  allPlayersReady: boolean;
}

const RaidContext = createContext<RaidContextValue | null>(null);

// ============================================================================
// Provider Component
// ============================================================================

export const RaidProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { getAccessToken, state: authState } = useAuth();
  const playerId =
    authState.status === "authenticated"
      ? authState.user.telegram_id
      : undefined;

  const [raidState, setRaidState] = useState<CoopRaidState>(initialRaidState);

  // Usable items state (potions/revives available during draft)
  const [usableItems, setUsableItems] = useState<UsableItem[]>([]);
  const [isLoadingItems, setIsLoadingItems] = useState(false);
  const [itemUseEvents, setItemUseEvents] = useState<ItemUseEvent[]>([]);

  /**
   * Clear an item use event (after animation/toast completes)
   */
  const clearItemUseEvent = useCallback((id: string) => {
    setItemUseEvents((prev) => prev.filter((e) => e.id !== id));
  }, []);

  // Ref to access current state in callbacks (like websocket handlers) without stale closures
  const raidStateRef = useRef(raidState);
  useEffect(() => {
    raidStateRef.current = raidState;
  }, [raidState]);

  const wsServiceRef = useRef<RaidWebSocketService | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [isReconnecting, setIsReconnecting] = useState(false);

  /**
   * Stop any active timer
   */
  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  /**
   * Fetch player's usable items (potions/revives) for draft phase
   */
  const fetchUsableItems = useCallback(async () => {
    const token = getAccessToken();
    if (!token) return;

    setIsLoadingItems(true);
    try {
      const response = await listInventoryItems(token);
      if (response.ok && response.data?.player_items) {
        // Filter to only raid-usable items
        const usable = response.data.player_items
          .filter((item) =>
            RAID_USABLE_ITEMS.includes(item.item_name as RaidUsableItem),
          )
          .map((item) => ({
            item_name: item.item_name as RaidUsableItem,
            amount: item.amount,
          }));
        setUsableItems(usable);
      }
    } catch (error) {
      console.error("[RaidProvider] Failed to fetch usable items:", error);
    } finally {
      setIsLoadingItems(false);
    }
  }, [getAccessToken]);

  /**
   * Decrement local item count after successful use (optimistic update)
   */
  const decrementItemCount = useCallback((itemName: string) => {
    setUsableItems((prev) =>
      prev
        .map((item) =>
          item.item_name === itemName
            ? { ...item, amount: item.amount - 1 }
            : item,
        )
        .filter((item) => item.amount > 0),
    );
  }, []);

  /**
   * Start countdown timer for room selection or draft
   */
  const startTimer = useCallback(
    (durationSeconds: number, phase: "room_selection" | "draft") => {
      stopTimer();
      const startTime = Date.now();

      timerRef.current = setInterval(() => {
        const elapsed = (Date.now() - startTime) / 1000;
        const remaining = Math.max(0, durationSeconds - elapsed);

        setRaidState((prev) => {
          if (phase === "room_selection" && prev.room_selection) {
            return {
              ...prev,
              room_selection: {
                ...prev.room_selection,
                time_remaining: remaining,
              },
            };
          }
          if (phase === "draft" && prev.draft) {
            return {
              ...prev,
              draft: { ...prev.draft, time_remaining: remaining },
            };
          }
          return prev;
        });

        if (remaining <= 0) {
          stopTimer();
        }
      }, 100);
    },
    [stopTimer],
  );

  /**
   * Handle incoming WebSocket messages
   */
  const handleMessage = useCallback(
    (message: ServerMessage) => {
      console.log(
        "[RaidProvider] Received message:",
        message.type,
        message.payload,
      );

      switch (message.type) {
        // ========== Initial State (on connect/reconnect) ==========
        case "raid_state": {
          const {
            raid_id,
            raid_name,
            player_role,
            status,
            current_round_index,
            current_step,
            host_id,
            players,
          } = message.payload;

          // Convert backend players to frontend format
          const convertedPlayers: Record<PlayerRole, RaidLobbyPlayer | null> = {
            player_a: null,
            player_b: null,
            player_c: null,
            player_d: null,
          };

          for (const role of [
            "player_a",
            "player_b",
            "player_c",
            "player_d",
          ] as PlayerRole[]) {
            const p = players[role];
            if (p) {
              convertedPlayers[role] = {
                id: p.id,
                role,
                username: p.username,
                country: p.country,
                team_preview: p.team.pokemon.map((poke: any) => ({
                  display_id: poke.display_id,
                  base_pokemon_name: poke.base_pokemon_name,
                  level: poke.level,
                })),
                is_ready: p.is_ready,
              };
            }
          }

          setRaidState((prev) => ({
            ...prev,
            status: mapRaidStatusToState(status),
            your_role: player_role,
            is_host: players[player_role]?.telegram_id === host_id,
            raid_id,
            raid_name,
            current_round: current_round_index,
            current_step,
            players: convertedPlayers,
          }));
          break;
        }

        // ========== Lobby Messages ==========
        case "player_joined": {
          const {
            player_role,
            player_id,
            player_name,
            player_country,
            pokemon_team,
          } = message.payload;

          setRaidState((prev) => ({
            ...prev,
            players: {
              ...prev.players,
              [player_role]: {
                id: player_id,
                role: player_role,
                username: player_name,
                country: player_country,
                team_preview: pokemon_team,
                is_ready: false,
              },
            },
          }));
          break;
        }

        case "player_left": {
          const { player_role } = message.payload;

          setRaidState((prev) => ({
            ...prev,
            players: {
              ...prev.players,
              [player_role]: null,
            },
          }));
          break;
        }

        case "host_changed": {
          const { new_host_id } = message.payload;

          setRaidState((prev) => {
            const myPlayer = prev.your_role
              ? prev.players[prev.your_role]
              : null;
            const amIHost = myPlayer?.id === new_host_id;

            return {
              ...prev,
              is_host: amIHost,
            };
          });
          break;
        }

        case "player_ready": {
          const { player_role, is_ready } = message.payload;

          setRaidState((prev) => {
            const player = prev.players[player_role];
            if (!player) return prev;

            return {
              ...prev,
              players: {
                ...prev.players,
                [player_role]: { ...player, is_ready },
              },
            };
          });
          break;
        }

        case "difficulty_changed": {
          const { difficulty } = message.payload;
          setRaidState((prev) => ({
            ...prev,
            difficulty,
          }));
          break;
        }

        case "raid_type_update": {
          setRaidState((prev) => ({
            ...prev,
            raid_name: message.payload.raid_name,
          }));
          break;
        }

        // ========== Raid Start ==========
        case "raid_started": {
          const { raid_name, difficulty, your_role, players } = message.payload;

          // Convert players to our format
          const convertedPlayers: Record<PlayerRole, RaidLobbyPlayer | null> = {
            player_a: players.player_a
              ? convertPlayerInfo(players.player_a, "player_a")
              : null,
            player_b: players.player_b
              ? convertPlayerInfo(players.player_b, "player_b")
              : null,
            player_c: players.player_c
              ? convertPlayerInfo(players.player_c, "player_c")
              : null,
            player_d: players.player_d
              ? convertPlayerInfo(players.player_d, "player_d")
              : null,
          };

          setRaidState((prev) => ({
            ...prev,
            status: "starting",
            raid_name,
            difficulty,
            your_role,
            players: convertedPlayers,
          }));
          break;
        }

        // ========== Round Start ==========
        case "round_started": {
          setRaidState((prev) => ({
            ...prev,
            current_round: message.payload.round_index,
            is_boss_round: message.payload.is_boss_round,
          }));
          break;
        }

        // ========== Room Selection ==========
        case "room_selection_started": {
          const { round_index, duration_seconds, room_options, started_at } =
            message.payload;

          setRaidState((prev) => ({
            ...prev,
            status: "room_selection",
            room_selection: {
              round_index,
              duration_seconds,
              room_options,
              votes: {} as Record<PlayerRole, number>,
              started_at,
              time_remaining: duration_seconds,
            },
          }));

          startTimer(duration_seconds, "room_selection");
          break;
        }

        case "room_vote_update": {
          const { player_role, room_index } = message.payload;

          setRaidState((prev) => {
            if (!prev.room_selection) return prev;

            return {
              ...prev,
              room_selection: {
                ...prev.room_selection,
                votes: {
                  ...prev.room_selection.votes,
                  [player_role]: room_index,
                },
              },
            };
          });
          break;
        }

        case "room_selection_result": {
          stopTimer();
          // Room selected, wait for draft_started
          break;
        }

        // ========== Draft Phase ==========
        case "draft_started": {
          const {
            round_index,
            duration_seconds,
            picks_required,
            available_pokemon,
            selected_room,
            started_at,
            slots,
          } = message.payload;

          setRaidState((prev) => ({
            ...prev,
            status: "draft",
            room_selection: null,
            draft: {
              round_index,
              duration_seconds,
              picks_required,
              available_pokemon,
              selected_room,
              picks: {} as Record<PlayerRole, number[]>,
              slots: slots || [],
              started_at,
              time_remaining: duration_seconds,
            },
          }));

          // Fetch usable items when draft starts
          fetchUsableItems();

          startTimer(duration_seconds, "draft");
          break;
        }

        case "draft_pick_update": {
          const { player_role, pokemon_id, slot_index } = message.payload;

          setRaidState((prev) => {
            if (!prev.draft) return prev;

            // Update slots
            const newSlots = prev.draft.slots.map((slot) => {
              if (slot.slot_index === slot_index) {
                return {
                  ...slot,
                  pokemon_id: pokemon_id ?? undefined,
                  picked_by: pokemon_id ? player_role : undefined,
                };
              }
              return slot;
            });

            // Rebuild picks map from slots
            const newPicksMap: Record<PlayerRole, number[]> = {
              player_a: [],
              player_b: [],
              player_c: [],
              player_d: [],
            };
            newSlots.forEach((slot) => {
              if (slot.pokemon_id && slot.picked_by) {
                newPicksMap[slot.picked_by].push(slot.pokemon_id);
              }
            });

            return {
              ...prev,
              draft: {
                ...prev.draft,
                slots: newSlots,
                picks: newPicksMap,
              },
            };
          });
          break;
        }

        case "item_used": {
          const {
            player_role,
            item_name,
            target_pokemon_id,
            new_hp,
            was_revived,
          } = message.payload;

          setRaidState((prev) => {
            const updates: Partial<CoopRaidState> = {};

            if (new_hp !== undefined) {
              updates.pokemon_hp = {
                ...prev.pokemon_hp,
                [target_pokemon_id]: new_hp,
              };
            }

            if (was_revived) {
              updates.fainted_pokemon = prev.fainted_pokemon.filter(
                (id) => id !== target_pokemon_id,
              );
            }

            // Find player and pokemon names for toast
            const playerInfo = prev.players[player_role];
            const pokemon = prev.draft?.available_pokemon?.find(
              (p) => p.id === target_pokemon_id,
            );

            if (playerInfo && pokemon) {
              const event: ItemUseEvent = {
                id: `${Date.now()}-${target_pokemon_id}`,
                playerRole: player_role,
                playerName: playerInfo.username,
                itemName: item_name,
                targetPokemonId: target_pokemon_id,
                targetPokemonName: pokemon.base_pokemon_name,
                newHp: new_hp,
                wasRevived: was_revived ?? false,
                timestamp: Date.now(),
              };
              setItemUseEvents((prevEvents) => [...prevEvents, event]);
            }

            return { ...prev, ...updates };
          });
          break;
        }

        case "draft_result": {
          stopTimer();
          // Draft complete, wait for battle_starting
          setRaidState((prev) => ({
            ...prev,
            draft: null,
          }));
          break;
        }

        // ========== Battle ==========
        case "battle_starting": {
          setRaidState((prev) => ({
            ...prev,
            status: "battle_starting",
          }));
          break;
        }

        case "battle_started": {
          const { round_index, battle_log_url } = message.payload;

          setRaidState((prev) => ({
            ...prev,
            status: "in_battle",
            current_battle: {
              round_index,
              battle_log_url,
              team_pokemon: [], // Will be populated from draft
            },
          }));
          break;
        }

        case "battle_result": {
          const { pokemon_hp, fainted_pokemon } = message.payload;

          setRaidState((prev) => ({
            ...prev,
            status: "battle_result",
            pokemon_hp: { ...prev.pokemon_hp, ...pokemon_hp },
            fainted_pokemon: [
              ...new Set([...prev.fainted_pokemon, ...fainted_pokemon]),
            ],
          }));
          break;
        }

        // ========== Raid End ==========
        case "raid_victory": {
          const { coins_earned, experience_earned, rewards, boss_drops } =
            message.payload;

          setRaidState((prev) => ({
            ...prev,
            status: "finished",
            result: {
              outcome: "victory",
              coins_earned,
              experience_earned,
              rewards: {
                recruitment_pool: rewards.recruitment_pool,
                items: rewards.items,
              },
              ...(boss_drops ? { boss_drops } : {}),
            },
          }));
          break;
        }

        case "raid_defeat": {
          const { coins_earned, experience_earned, defeat_round } =
            message.payload;

          setRaidState((prev) => ({
            ...prev,
            status: "finished",
            result: {
              outcome: "defeat",
              coins_earned,
              experience_earned,
              defeat_round,
            },
          }));
          break;
        }

        case "raid_abandoned": {
          const { quitter_name } = message.payload;

          setRaidState((prev) => ({
            ...prev,
            status: "finished",
            result: {
              outcome: "abandoned",
              coins_earned: 0,
              experience_earned: 0,
              quitter_name,
            },
          }));
          break;
        }

        // ========== Error & Connection ==========
        case "error": {
          setRaidState((prev) => ({
            ...prev,
            status: "error",
            error: {
              code: message.payload.code,
              message: message.payload.message,
            },
          }));
          break;
        }

        case "connection_status": {
          console.log(
            "[RaidProvider] Connection status:",
            message.payload.status,
          );
          if (message.payload.status === "reconnected") {
            setIsReconnecting(false);
          }
          break;
        }

        default:
          console.warn(
            "[RaidProvider] Unknown message type:",
            (message as any).type,
          );
      }
    },
    [startTimer, stopTimer, fetchUsableItems],
  );

  /**
   * Handle connection status changes
   */
  const handleConnectionChange = useCallback(
    (status: WebSocketConnectionStatus) => {
      console.log("[RaidProvider] Connection status changed:", status);
      setRaidState((prev) => ({
        ...prev,
        connection_status:
          status === "connected" ? "connected" : "disconnected",
      }));

      if (status === "disconnected") {
        stopTimer();
      }
    },
    [stopTimer],
  );

  /**
   * Handle WebSocket errors
   */
  const handleError = useCallback((error: Error) => {
    console.error("[RaidProvider] WebSocket error:", error);
    setRaidState((prev) => ({
      ...prev,
      status: "error",
      error: {
        code: "WEBSOCKET_ERROR",
        message: error.message,
      },
    }));
  }, []);

  /**
   * Connect to raid WebSocket
   */
  const connectToRaid = useCallback(
    async (
      raidId: string,
      playerId: string,
      token: string,
      websocketUrl: string,
    ) => {
      try {
        setRaidState((prev) => ({ ...prev, status: "connecting" }));

        const wsService = new RaidWebSocketService({
          url: websocketUrl,
          raid_id: raidId,
          player_id: playerId,
          token,
        });

        wsService.onMessage(handleMessage);
        wsService.onConnectionChange(handleConnectionChange);
        wsService.onError(handleError);

        await wsService.connect();

        wsServiceRef.current = wsService;
        setRaidState((prev) => ({
          ...prev,
          status: "lobby",
          raid_id: raidId,
        }));
      } catch (error) {
        console.error("[RaidProvider] Failed to connect:", error);
        setRaidState((prev) => ({
          ...prev,
          status: "error",
          error: {
            code: "CONNECTION_FAILED",
            message:
              error instanceof Error ? error.message : "Failed to connect",
          },
        }));
      }
    },
    [handleMessage, handleConnectionChange, handleError],
  );

  /**
   * Check for existing raid on mount/auth
   */
  useEffect(() => {
    const checkRaid = async () => {
      const token = getAccessToken();
      if (!token || !playerId || raidState.raid_id) return;

      try {
        const response = await getCurrentRaid(token);
        if (
          response.ok &&
          response.data?.raid_id &&
          raidState.status === "idle"
        ) {
          const wsUrl = import.meta.env.VITE_RAID_WS_API_URL;
          if (wsUrl) {
            console.log(
              "[RaidProvider] Found active raid, reconnecting:",
              response.data.raid_id,
            );
            await connectToRaid(response.data.raid_id, playerId, token, wsUrl);
          }
        }
      } catch (error) {
        console.error("[RaidProvider] Failed to check raid status:", error);
      }
    };

    checkRaid();
  }, [
    getAccessToken,
    playerId,
    connectToRaid,
    raidState.raid_id,
    raidState.status,
  ]);

  /**
   * Create a new raid lobby
   */
  const createRaidLobby = useCallback(
    async (
      token: string,
      playerId: string,
      raidConfig: RaidConfig,
      difficulty: RaidDifficulty,
    ) => {
      try {
        setRaidState((prev) => ({ ...prev, status: "connecting", difficulty }));

        // Get raid name from type
        const raidName = raidConfig.name;

        const response = await createRaid(token, {
          raid_name: raidName,
          difficulty,
        });

        if (!response.ok || !response.data?.raid_id) {
          const errorMessage = response.message || "Failed to create raid";
          setRaidState((prev) => ({
            ...prev,
            status: "error",
            error: { code: "CREATE_FAILED", message: errorMessage },
          }));
          return { success: false, error: errorMessage };
        }

        const { raid_id, join_code } = response.data;
        const wsUrl = import.meta.env.VITE_RAID_WS_API_URL;

        if (!wsUrl) {
          console.error("Missing VITE_RAID_WS_API_URL");
          setRaidState((prev) => ({
            ...prev,
            status: "error",
            error: {
              code: "CONFIG_ERROR",
              message: "WebSocket URL not configured",
            },
          }));
          return { success: false, error: "WebSocket URL not configured" };
        }

        setRaidState((prev) => ({
          ...prev,
          invite_code: join_code || null,
          raid_type: raidConfig.type,
          raid_name: raidConfig.name,
          is_host: true,
          your_role: "player_a",
        }));

        await connectToRaid(raid_id, playerId, token, wsUrl);

        return join_code
          ? { success: true, invite_code: join_code }
          : { success: true };
      } catch (error) {
        console.error("[RaidProvider] Failed to create raid:", error);
        const errorMessage =
          error instanceof Error ? error.message : "Failed to create raid";
        setRaidState((prev) => ({
          ...prev,
          status: "error",
          error: { code: "CREATE_FAILED", message: errorMessage },
        }));
        return { success: false, error: errorMessage };
      }
    },
    [connectToRaid],
  );

  /**
   * Join an existing raid lobby
   */
  const joinRaidLobby = useCallback(
    async (token: string, playerId: string, inviteCode: string) => {
      try {
        setRaidState((prev) => ({ ...prev, status: "connecting" }));

        const response = await joinRaidService(token, {
          join_code: inviteCode,
        });

        if (!response.ok || !response.data?.raid_id) {
          const errorMessage = response.message || "Failed to join raid";
          setRaidState((prev) => ({
            ...prev,
            status: "error",
            error: { code: "JOIN_FAILED", message: errorMessage },
          }));
          return { success: false, error: errorMessage };
        }

        const { raid_id, player_role } = response.data;
        const wsUrl = import.meta.env.VITE_RAID_WS_API_URL;

        if (!wsUrl) {
          console.error("Missing VITE_RAID_WS_API_URL");
          setRaidState((prev) => ({
            ...prev,
            status: "error",
            error: {
              code: "CONFIG_ERROR",
              message: "WebSocket URL not configured",
            },
          }));
          return { success: false, error: "WebSocket URL not configured" };
        }

        setRaidState((prev) => ({
          ...prev,
          invite_code: inviteCode,
          is_host: false,
          your_role: player_role,
        }));

        await connectToRaid(raid_id, playerId, token, wsUrl);

        return { success: true };
      } catch (error) {
        console.error("[RaidProvider] Failed to join raid:", error);
        const errorMessage =
          error instanceof Error ? error.message : "Failed to join raid";
        setRaidState((prev) => ({
          ...prev,
          status: "error",
          error: { code: "JOIN_FAILED", message: errorMessage },
        }));
        return { success: false, error: errorMessage };
      }
    },
    [connectToRaid],
  );

  /**
   * Toggle ready state in lobby
   */
  const toggleReady = useCallback(() => {
    if (!wsServiceRef.current || !raidState.raid_id) return;

    const currentPlayer = raidState.your_role
      ? raidState.players[raidState.your_role]
      : null;
    const newReadyState = !currentPlayer?.is_ready;

    wsServiceRef.current.send({
      action: "ready",
      payload: {
        raid_id: raidState.raid_id,
        is_ready: newReadyState,
      },
    });
  }, [raidState.raid_id, raidState.your_role, raidState.players]);

  /**
   * Set raid difficulty (host only)
   * Sends update to server which broadcasts to all players
   */
  const setDifficulty = useCallback(
    (difficulty: RaidDifficulty) => {
      if (!wsServiceRef.current || !raidState.raid_id || !raidState.is_host)
        return;

      wsServiceRef.current.send({
        action: "set_difficulty",
        payload: {
          raid_id: raidState.raid_id,
          difficulty,
        },
      });
    },
    [raidState.raid_id, raidState.is_host],
  );

  /**
   * Start the raid (host only)
   */
  const startRaid = useCallback(() => {
    if (!wsServiceRef.current || !raidState.raid_id || !raidState.is_host)
      return;

    wsServiceRef.current.send({
      action: "start_raid",
      payload: {
        raid_id: raidState.raid_id,
      },
    });
  }, [raidState.raid_id, raidState.is_host]);

  /**
   * Disconnect from raid and reset state
   */
  const disconnectFromRaid = useCallback(() => {
    if (wsServiceRef.current) {
      wsServiceRef.current.destroy();
      wsServiceRef.current = null;
    }

    stopTimer();
    setRaidState(initialRaidState);
    setIsReconnecting(false);
  }, [stopTimer]);

  /**
   * Leave the raid (lobby) or quit (active raid)
   */
  const leaveRaid = useCallback(() => {
    if (wsServiceRef.current && raidState.raid_id) {
      // Use leave_lobby in lobby phase, quit_raid for active raid
      const action = raidState.status === "lobby" ? "leave_lobby" : "quit_raid";
      wsServiceRef.current.send({
        action,
        payload: {
          raid_id: raidState.raid_id,
        },
      });
      // Small delay to ensure message is sent before disconnecting
      setTimeout(() => disconnectFromRaid(), 100);
    } else {
      disconnectFromRaid();
    }
  }, [raidState.raid_id, raidState.status, disconnectFromRaid]);

  /**
   * Vote for a room option
   */
  const voteForRoom = useCallback(
    (roomIndex: number) => {
      if (
        !wsServiceRef.current ||
        !raidState.raid_id ||
        !raidState.room_selection
      )
        return;

      wsServiceRef.current.send({
        action: "player_action",
        payload: {
          raid_id: raidState.raid_id!,
          __typename: "PlayerActionRoomVotePayload",
          round_index: raidState.room_selection.round_index,
          room_index: roomIndex,
        },
      });
    },
    [raidState.raid_id, raidState.room_selection],
  );

  /**
   * Pick a pokemon during draft
   */
  const pickPokemon = useCallback(
    (pokemonId: number, slotIndex: number) => {
      if (!wsServiceRef.current || !raidState.raid_id || !raidState.draft)
        return;

      wsServiceRef.current.send({
        action: "player_action",
        payload: {
          raid_id: raidState.raid_id!,
          __typename: "PlayerActionPokemonPickPayload",
          round_index: raidState.draft.round_index,
          pokemon_id: pokemonId,
          slot_index: slotIndex,
        },
      });
    },
    [raidState.raid_id, raidState.draft],
  );

  /**
   * Unpick a pokemon during draft
   * @deprecated Use pickPokemon with same ID to toggle off
   */
  const unpickPokemon = useCallback((pokemonId: number) => {
    // no-op, removed
  }, []);

  /**
   * Submit final pokemon picks
   */
  const submitPicks = useCallback(() => {
    if (
      !wsServiceRef.current ||
      !raidState.raid_id ||
      !raidState.draft ||
      !raidState.your_role
    )
      return;

    const myPicks = raidState.draft.picks[raidState.your_role] || [];

    wsServiceRef.current.send({
      action: "player_action",
      payload: {
        raid_id: raidState.raid_id!,
        __typename: "PlayerActionSubmitPicksPayload",
        round_index: raidState.draft.round_index,
        pokemon_ids: myPicks,
      },
    });
  }, [raidState.raid_id, raidState.draft, raidState.your_role]);

  /**
   * Use an item during draft
   */
  const useItem = useCallback(
    (itemName: string, targetPokemonId: number) => {
      if (!wsServiceRef.current || !raidState.raid_id || !raidState.draft)
        return;

      // Validate item name
      if (!RAID_USABLE_ITEMS.includes(itemName as RaidUsableItem)) {
        console.error("[RaidProvider] Invalid item name:", itemName);
        return;
      }

      // Check if player has the item
      const item = usableItems.find((i) => i.item_name === itemName);
      if (!item || item.amount <= 0) {
        console.error("[RaidProvider] Item not available:", itemName);
        return;
      }

      wsServiceRef.current.send({
        action: "player_action",
        payload: {
          raid_id: raidState.raid_id!,
          __typename: "PlayerActionItemUsePayload",
          round_index: raidState.draft.round_index,
          item_name: itemName as RaidUsableItem,
          target_pokemon_id: targetPokemonId,
        },
      });

      // Optimistic update - decrement item count locally
      decrementItemCount(itemName);
    },
    [raidState.raid_id, raidState.draft, usableItems, decrementItemCount],
  );

  /**
   * Acknowledge battle end (ready for next round)
   */
  const acknowledgeBattleEnd = useCallback(() => {
    // Guard: need battle context to send ACK
    if (
      !wsServiceRef.current ||
      !raidState.raid_id ||
      !raidState.current_battle
    ) {
      return;
    }

    // Use battle's round_index to ensure we ACK the correct round
    const roundIndex = raidState.current_battle.round_index;

    wsServiceRef.current.sendVisualAck(raidState.raid_id, roundIndex);

    setRaidState((prev) => ({
      ...prev,
      current_battle: null,
    }));
  }, [raidState.raid_id, raidState.current_battle]);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      if (wsServiceRef.current) {
        wsServiceRef.current.destroy();
      }
      stopTimer();
    };
  }, [stopTimer]);

  // Computed values - host (player_a) is excluded since they don't need to ready up
  const allPlayersReady = useMemo(() => {
    const guestPlayers = Object.entries(raidState.players)
      .filter(([role, p]) => role !== "player_a" && p !== null)
      .map(([, p]) => p);
    return guestPlayers.length > 0 && guestPlayers.every((p) => p?.is_ready);
  }, [raidState.players]);

  const canStart = useMemo(() => {
    if (!raidState.is_host || raidState.status !== "lobby") return false;

    // Ensure host is present in the list before allowing start
    if (!raidState.players.player_a) return false;

    const guestPlayers = Object.entries(raidState.players)
      .filter(([role, p]) => role !== "player_a" && p !== null)
      .map(([, p]) => p);

    // If no guest players, host can start solo
    if (guestPlayers.length === 0) return true;

    // If there are guest players, they all need to be ready
    return guestPlayers.every((p) => p?.is_ready);
  }, [raidState.is_host, raidState.status, raidState.players]);

  const value = useMemo<RaidContextValue>(
    () => ({
      raidState,
      setRaidState,
      createRaidLobby,
      joinRaidLobby,
      toggleReady,
      setDifficulty,
      startRaid,
      leaveRaid,
      voteForRoom,
      pickPokemon,
      unpickPokemon,
      submitPicks,
      useItem,
      acknowledgeBattleEnd,
      disconnectFromRaid,
      isConnected: raidState.connection_status === "connected",
      isReconnecting,
      canStart,
      allPlayersReady,
      usableItems,
      isLoadingItems,
      itemUseEvents,
      clearItemUseEvent,
    }),
    [
      raidState,
      createRaidLobby,
      joinRaidLobby,
      toggleReady,
      setDifficulty,
      startRaid,
      leaveRaid,
      voteForRoom,
      pickPokemon,
      unpickPokemon,
      submitPicks,
      useItem,
      acknowledgeBattleEnd,
      disconnectFromRaid,
      isReconnecting,
      canStart,
      allPlayersReady,
      usableItems,
      isLoadingItems,
      itemUseEvents,
      clearItemUseEvent,
    ],
  );

  return <RaidContext.Provider value={value}>{children}</RaidContext.Provider>;
};

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook to access raid context
 */
export function useRaid() {
  const ctx = useContext(RaidContext);
  if (!ctx) throw new Error("useRaid must be used within <RaidProvider>");
  return ctx;
}

/**
 * Hook to access raid connection state
 */
export function useRaidConnection() {
  const { isConnected, raidState } = useRaid();
  return {
    isConnected,
    connectionStatus: raidState.connection_status,
    status: raidState.status,
  };
}

/**
 * Hook to access lobby state
 */
export function useRaidLobby() {
  const {
    raidState,
    toggleReady,
    setDifficulty,
    startRaid,
    leaveRaid,
    canStart,
  } = useRaid();

  return {
    players: raidState.players,
    isHost: raidState.is_host,
    yourRole: raidState.your_role,
    difficulty: raidState.difficulty,
    raidName: raidState.raid_name,
    inviteCode: raidState.invite_code,
    canStart,
    toggleReady,
    setDifficulty,
    startRaid,
    leaveRaid,
  };
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Map backend RaidStatus to frontend CoopRaidState status
 */
function mapRaidStatusToState(status: RaidStatus): CoopRaidState["status"] {
  switch (status) {
    case "lobby":
      return "lobby";
    case "starting":
      return "starting";
    case "room_selection":
      return "room_selection";
    case "draft":
      return "draft";
    case "battle":
      return "in_battle";
    case "battle_results":
      return "battle_result";
    case "victory":
    case "defeat":
    case "abandoned":
      return "finished";
    default:
      return "lobby";
  }
}

/**
 * Convert RaidPlayerInfo to RaidLobbyPlayer
 */
function convertPlayerInfo(
  info: RaidPlayerInfo,
  role: PlayerRole,
): RaidLobbyPlayer {
  return {
    id: info.id,
    role,
    username: info.username,
    country: info.country,
    team_preview: info.team.pokemon.map((p) => ({
      display_id: p.display_id,
      base_pokemon_name: p.base_pokemon_name,
      level: p.level,
    })),
    is_ready: true, // Players are ready when raid starts
  };
}
